import { useEffect, useRef, useState } from 'react'
import { UserMenu } from '@proj-client/components/user/userMenu'
import IdeateLiveLogoBlack from '@proj-client/assets/imgs/nav/ideatelive_logo_black.svg'
import IdeateLiveLogoSmall from '@proj-client/assets/imgs/nav/ideatelive_logo_notext_color.svg'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'

interface NavBarItem {
    url?: string,
    text: string,
    accent?: string,
    clickHandler?: () => void
}

interface IProps {
    pageTitle?: string,
    pageTitleMenuContentEl?: JSX.Element,
    mainItems: NavBarItem[],
    hamburgerItems: NavBarItem[],
    userMenu: boolean,
    fullWidth: boolean,
    hasBorder?: boolean,
    isSticky?: boolean,
}

export const NavBar = ({ hasBorder = true,
    pageTitle,
    pageTitleMenuContentEl,
    mainItems,
    hamburgerItems,
    userMenu,
    fullWidth = true,
    isSticky = false
}: IProps) => {

    const navbarRef = useRef(null)

    useEffect(() => {
        if (!isSticky || !hidden) return;
        const handleScroll = () => {
          const scrollPos = window.scrollY;
          const maxOpacityChangePos = 250; // Change this to control at what scroll position the navbar becomes fully opaque
          const opacity = Math.min(scrollPos / maxOpacityChangePos, 1);
          if (navbarRef.current) {
            navbarRef.current.style.backgroundColor = `rgba(255, 255, 255, ${opacity})`; // Change the background color to white with the calculated opacity
          }
        };
    
        window.addEventListener('scroll', handleScroll);
    
        // Clean up the event listener when the component unmounts
        return () => {
          window.removeEventListener('scroll', handleScroll);
        };
      }, [isSticky]);

    const [hidden, setHidden] = useState(true)

    const hamburgerPress = e => {
        setHidden(!hidden)
    }

    return (
        <>
            {
                // if isSticky a spacer div is added to prevent the content from jumping up
            }
            {isSticky && <div className="h-[44px]"></div>}
            <div ref={navbarRef} className={`w-full ${isSticky ? "fixed top-0 left-0 right-0 " : "bg-white"} ${isSticky && !hidden ? "bg-white"  : ""} ${hasBorder ? "border-b border-slate-200" : ""}`}>
                <div className={`${fullWidth ? "w-full" : "container mx-auto "} md:py-2 py-0   `}>

                    <nav className="relative flex items-center justify-between px-2 md:px-4 min-h-[44px]" aria-label="Global">
                        <div className="flex items-center flex-grow flex-shrink-0 h-full">
                            <div className="flex items-center justify-between md:justify-start w-full h-full ">

                                <a href="/" aria-label='Home'>
                                    {pageTitle ?
                                        <IdeateLiveLogoSmall className="w-auto h-8 md:mr-4" />
                                        :
                                        <IdeateLiveLogoBlack className="w-auto md:h-5 h-4" />
                                    }
                                </a>

                                {
                                    pageTitle && !pageTitleMenuContentEl && (<div className=' font-bold text-lg md:text-xl'>
                                        <h1>{pageTitle}</h1>
                                    </div>)
                                }

                                { /* PAGE TITLE */}
                                {pageTitle && pageTitleMenuContentEl && (
                                    //<div className=' font-bold text-lg md:text-xl'>
                                    //    <h1>{pageTitle}</h1>
                                    //</div>

                                    <DropdownMenu.Root>
                                        <DropdownMenu.Trigger asChild>
                                            <button aria-label={`${pageTitle} menu`} className='idtn-btn-xl idtn-btn-naked font-bold text-lg md:text-xl flex space-x-1 items-center'>
                                                <h1 className='uppercase block md:hidden'>
                                                    {pageTitle.length > 15 ? pageTitle.substring(0, 15).trimEnd() + '...' : pageTitle.trimEnd()}
                                                </h1>
                                                <h1 className='uppercase hidden md:block'>
                                                    {pageTitle}
                                                </h1>
                                                <i className="fa-solid fa-chevron-down text-sm text-slate-600"></i>
                                            </button>
                                        </DropdownMenu.Trigger>
                                        <DropdownMenu.Portal>
                                            <DropdownMenu.Content
                                                className="z-50 min-w-[220px] bg-white rounded-md p-[5px] text-base border-slate-200 border shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade "
                                                sideOffset={12}
                                            >
                                                <DropdownMenu.Label className="nav-dropdown-item text-slate-600 text-sm break-words max-w-[220px]">{pageTitle}</DropdownMenu.Label>

                                                {
                                                    // radix separator line
                                                }
                                                <DropdownMenu.Separator className="my-2 border-b border-slate-100 border" />

                                                {pageTitleMenuContentEl}

                                            </DropdownMenu.Content>
                                        </DropdownMenu.Portal>
                                    </DropdownMenu.Root>


                                )}

                                <div className="flex md:flex-col md:flex-grow md:items-end items-center">

                                    { /* MAIN ITEMS */}
                                    {mainItems.map((item, idx) => (
                                        <a key={`main-${idx}`} href={item.url} className={`idtn-btn-md ${item.accent || ''} mr-2 my-2`}>{item.text}</a>
                                    ))}

                                    { /* HAMBURGER */}
                                    <div className="md:hidden">
                                        <button onClick={hamburgerPress} type="button" className="inline-flex items-center justify-center p-1.5 text-gray-400 bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 active:bg-white" aria-expanded="false">
                                            <span className="sr-only">Open main menu</span>

                                            <svg className="h-5 w-5 md:w-6 md:h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                                            </svg>
                                        </button>
                                    </div>

                                </div>
                            </div>
                        </div>

                        { /* HAMBURGER ITEMS - desktop */}
                        <div className={`hidden md:flex justify-self-center content-center md:gap-4 `}>
                            {hamburgerItems.map((item, idx) => (
                                <a key={`ham-${idx}`} href={item.url} onClick={item.clickHandler} className={`idtn-btn-md ${item.accent || ''}`}>{item.text}</a>
                            ))}
                            {hidden ? userMenu && <UserMenu /> : null}
                        </div>
                    </nav>

                    { /* HAMBURGER ITEMS - mobile */}
                    <div className={`${hidden ? "hidden" : " md:hidden grid grid-cols-1 justify-self-end pt-2 mt-4 border-t border-slate-200 bg-white"} `}>
                        {hamburgerItems.map((item, idx) => (
                            <a key={`ham-mobile-${idx}`} href={item.url} className={`py-1 pr-1 my-1 text-base text-right`}>{item.text}</a>
                        ))}
                        {hidden ? null : userMenu && <UserMenu />}
                    </div>

                </div>

            </div>
        </>
    )
}